import React from "react";
import { Button, Container } from "@material-ui/core";
import { CTextField, HFlex, VFlex, Text, useErrorContext, ProgressButton } from "shared";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { getDate } from "utils/time";
import { useQueryClient } from "react-query";
import useUpdateActivity from "../../queries/useUpdateActivity";
import { useId, useMountState } from "utils/hooks";
import { fullName, queryKeys } from "utils/misc";
import usePatient from "routes/patient/queries/usePatient";
import { FontSize } from "theme";
import { css } from "@emotion/css";
import { definitions } from "api";
import { Vars } from "../../queries/useUpdateActivity";

type Activity = definitions["activity"];

interface Props {
  activity: Activity;
  onClose: () => void;
}

const HealthRerportForm: React.VFC<Props> = ({ activity, onClose }) => {
  const id = useId();
  const { t } = useTranslation();
  const form = useForm<Vars>();
  const setError = useErrorContext();
  // @ts-ignore
  const { mutateAsync, isLoading } = useUpdateActivity(activity.user_activity_id);
  const getMountState = useMountState();
  const queryClient = useQueryClient();
  const { data: patient } = usePatient();

  if (!patient) return null;

  const onSave = form.handleSubmit(async ({ specific_joint_pain }) => {
    try {
      await mutateAsync({ specific_joint_pain });
      queryClient.invalidateQueries(queryKeys.schedule(id, activity.course_week_number!));
      getMountState() && onClose();
    } catch (err) {
      setError(t`errors.could_not_update_activity`, err);
    }
  });

  return (
    <Container>
      <Text heading bold id="edit-activity">
        {t`form.health_report.header`}
      </Text>
      <Text paragraph mb={2}>
        {fullName(patient)}, <Text link>{patient.id}</Text>
      </Text>
      <Text paragraph disabled mb={6}>
        {getDate(activity.start_date)}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={onSave}>
          <HFlex justifyContent="center" mb={6}>
            <CTextField
              asNumber
              autoFocus
              autoSelect
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: 0,
                max: 10,
                className: styles.input,
              }}
              type="number"
              defaultValue={activity?.specific_joint_pain ?? null}
              required
              name="specific_joint_pain"
              noClear
              label={t`form.pain_rating`}
            />
          </HFlex>
          <VFlex alignItems="center" gap={2} mb={5}>
            <ProgressButton loading={isLoading} type="submit" variant="contained" color="primary">
              {t`buttons.save`}
            </ProgressButton>
            <Button onClick={onClose}>{t`buttons.cancel`}</Button>
          </VFlex>
        </form>
      </FormProvider>
    </Container>
  );
};

export default HealthRerportForm;

const styles = {
  input: css`
    width: 125px !important;
    font-size: ${FontSize.xl}px !important;
    text-align: center !important;
  `,
};
